import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useRef } from 'react';
import RealTimeTranscription from './RealTimeTranscription';
import './ClientDashboard.css'; // Assuming you will add styles
import AITemplateManager from './AITemplateManager';
import { Capacitor } from '@capacitor/core';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { useAIConsent } from './utils/useAIConsent';
import AIConsentModal from './AIConsentModal';


let recordingStream; // Declare the recording stream at the top of the file
let lastTranscriptionTime = Date.now();

const ClientDashboard = () => {
  const { clientId } = useParams(); // Extract the client ID from the URL
  const [clientData, setClientData] = useState(null);
  const [isPro, setIsPro] = useState(true); // default true to not block on load
  const { consentGiven, showConsentModal, requireConsent, handleAccept, handleDecline } = useAIConsent();
  const [searchQuery, setSearchQuery] = useState('');
  const [isClientInfoExpanded, setIsClientInfoExpanded] = useState(false);

  // Show consent modal on mount if not yet given
  useEffect(() => {
    if (consentGiven === false) {
      requireConsent(() => {});
    }
  }, [consentGiven]);
  const [newSessionNote, setNewSessionNote] = useState('');
  const [selectedNote, setSelectedNote] = useState(null); // State to hold the selected note for viewing
  const [editNoteText, setEditNoteText] = useState(''); // State to hold the note being edited
  const [isEditingNote, setIsEditingNote] = useState(false); // State to track if a note is being edited
  const [errorMessage, setErrorMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isRecording, setIsRecording] = useState(false); // To manage recording state
  const [transcription, setTranscription] = useState(''); // To store transcription
  const [showSessionModal, setShowSessionModal] = useState(false); // State for session expiration modal
  const [renameModal, setRenameModal] = useState({ show: false, noteId: null, currentTitle: '' });
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });
  const [isLoading, setIsLoading] = useState(false); // For loading indicator
  const [noteSummaries, setNoteSummaries] = useState({}); // To store individual note summaries
  const [tonePreference, setTonePreference] = useState('professional');
  const [customPrompt, setCustomPrompt] = useState('');
  const [expandedNotes, setExpandedNotes] = useState({});
  const [loadingSummaries, setLoadingSummaries] = useState({});
  const [aiTemplates, setAiTemplates] = useState([]);
  const [actionItems, setActionItems] = useState({});
  const [loadingActions, setLoadingActions] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [loadingCoachAnalyses, setLoadingCoachAnalyses] = useState({});
  const [loadingCustomAnalyses, setLoadingCustomAnalyses] = useState({});
  const [expandedSections, setExpandedSections] = useState({}); // State to track expanded sections per note
  const [expandedSummaries, setExpandedSummaries] = useState({}); // To store whether each summary is expanded or collapsed
  const [isAnalysisSectionExpanded, setIsAnalysisSectionExpanded] = useState(false);
  const [loadingStates, setLoadingStates] = useState({}); // Track loading states per note
  const [analysisStates, setAnalysisStates] = useState({}); // Track loading state for Coach Analysis
  const [noteAnalyses, setNoteAnalyses] = useState({}); // Store analyses for each note
  const [expandedAnalyses, setExpandedAnalyses] = useState({}); // Store whether each analysis is expanded or collapsed
  const [notification, setNotification] = useState('');
  const [showSavedModal, setShowSavedModal] = useState(false);
  const [savedModalMessage, setSavedModalMessage] = useState('');
  const [showBillingPrompt, setShowBillingPrompt] = useState(false);
  const [billingPromptData, setBillingPromptData] = useState(null);
  const [customCriteria, setCustomCriteria] = useState('');
  const [customAnalysis, setCustomAnalysis] = useState({});
  const [isViewingSummary, setIsViewingSummary] = useState(false); // New state to track if viewing summary
  const [customAnalyses, setCustomAnalyses] = useState({});
  const [expandedCustomAnalyses, setExpandedCustomAnalyses] = useState({});
  const [isPaused, setIsPaused] = useState(false);
  const [transcriptionStatus, setTranscriptionStatus] = useState('idle'); // 'idle', 'active', 'hung'
  const [processingTranscript, setProcessingTranscript] = useState(false);

  const [savingSummaryId, setSavingSummaryId] = useState(null);   // noteId currently saving
  const [justSavedSummaryId, setJustSavedSummaryId] = useState(null); // noteId just confirmed saved

  const [sendTranscriptModal, setSendTranscriptModal] = useState({ show: false, noteId: null });
  const [sendingTranscript, setSendingTranscript] = useState(false);
  
  // Stealth mode & wake lock
  const [isStealthMode, setIsStealthMode] = useState(false);
  const isNative = Capacitor.isNativePlatform();
  const lastTranscriptionTimeRef = useRef(Date.now());

  // AI Chat Assistant states
  const [showAIChat, setShowAIChat] = useState(false);
  const [aiChatInput, setAiChatInput] = useState('');
  const [aiChatMessages, setAiChatMessages] = useState([]);
  const [aiChatLoading, setAiChatLoading] = useState(false);
  const [selectedNotesForAI, setSelectedNotesForAI] = useState([]);
  const [aiChatResponse, setAiChatResponse] = useState(null);
  const aiChatInputRef = useRef(null);

  // ChatGPT Launcher states
  const [showChatGPTLauncher, setShowChatGPTLauncher] = useState(false);
  const [chatGPTPopup, setChatGPTPopup] = useState(null);
  const [copiedNoteId, setCopiedNoteId] = useState(null);
  const [activeNoteTab, setActiveNoteTab] = useState('view'); // 'view' | 'add'
  const [externalAIUrl, setExternalAIUrl] = useState('');
  const [savingExternalAI, setSavingExternalAI] = useState(false);
  const recordingStartTimeRef = useRef(null); // track recording start for billing duration

  const [showTranscriptionConsentReminder, setShowTranscriptionConsentReminder] = useState(false);
  const [pendingRecordingFn, setPendingRecordingFn] = useState(null);

  const [consentReminderEnabled, setConsentReminderEnabled] = useState(true);
  
const handleStartRecordingWithReminder = (recordFn) => {
  if (consentReminderEnabled) {
    setShowTranscriptionConsentReminder(true);
    setPendingRecordingFn(() => recordFn);
  } else {
    recordFn(); // skip reminder, start directly
  }
};

  // Wake lock — keep screen on during transcription
  const enableWakeLock = async () => {
    if (!isNative) return;
    try { await KeepAwake.keepAwake(); } catch (e) { console.warn('KeepAwake enable error:', e); }
  };

  const disableWakeLock = async () => {
    if (!isNative) return;
    try { await KeepAwake.allowSleep(); } catch (e) { console.warn('KeepAwake disable error:', e); }
  };

  // Stealth mode — darken screen to near-black overlay
  const toggleStealthMode = () => {
    setIsStealthMode(prev => !prev);
  };

  // Clean up wake lock and stealth mode when component unmounts
  useEffect(() => {
    return () => {
      disableWakeLock();
      setIsStealthMode(false);
    };
  }, []);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const recordingIntervalRef = useRef(null);
  const recordingStateRef = useRef(false);
  
  // Diarization (speaker identification) toggle
  const [diarizeEnabled, setDiarizeEnabled] = useState(false);
  
  // Browser-based transcription states
  const [recognitionInstance, setRecognitionInstance] = useState(null); // Store the speech recognition instance
  
  const micStream = useRef(null);
  const transcriptionWatchdogInterval = 10000; // check every 10 seconds (adjust as needed)
  const transcriptionTimeoutThreshold = 30000; // if no text for 30 seconds, trigger a restart
  const [isViewingAnalysis, setIsViewingAnalysis] = useState(false); // State to track if viewing coach analysis
  const [isViewingCustomAnalysis, setIsViewingCustomAnalysis] = useState(false); // State to track if viewing custom analysis
  const [remainingTime, setRemainingTime] = useState(null); // Time left before token expires
  const navigate = useNavigate(); // Navigation hook for handling navigation
  const token = localStorage.getItem('token');
  const noteTextareaRef = useRef(null);
  const [user, setUser] = useState(null);

  let watchdogTimer = null;

// Modify `toggleCustomAnalysis` to add toggling behavior
const toggleCustomAnalysis = (noteId) => {
  if (selectedNote && selectedNote._id === noteId && isViewingCustomAnalysis) {
    // If currently viewing custom analysis, toggle back to the note
    setIsViewingCustomAnalysis(false);
    setExpandedCustomAnalyses((prevState) => ({
      ...prevState,
      [noteId]: false, // Set the expanded state for this note to false
    }));
  } else {
    // Otherwise, set to viewing custom analysis mode and deactivate others
    setIsViewingCustomAnalysis(true);
    setIsViewingSummary(false);
    setIsViewingAnalysis(false);

    setExpandedCustomAnalyses((prevState) => ({
      ...prevState,
      [noteId]: true, // Set the expanded state for this note to true
    }));
    setExpandedSummaries({});
    setExpandedAnalyses({});
    setSelectedNote(clientData.sessionNotes.find((note) => note._id === noteId)); // Ensure the selected note is set correctly
  }
};

  const fetchClientData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClientData(response.data);

      // Load consent reminder preference
try {
  const consentRes = await axios.get(
    `${process.env.REACT_APP_API_URL}/api/consent/client/${clientId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (consentRes.data?.consent?.reminderEnabled !== undefined) {
    setConsentReminderEnabled(consentRes.data.consent.reminderEnabled);
  }
} catch (e) {
  console.warn('Could not load consent reminder preference');
}
  
      // Extract summaries, coach analyses, and custom analyses from sessionNotes
      const summaries = {};
      const coachAnalyses = {};
      const customAnalyses = {}; // New state for custom analyses
  
      response.data.sessionNotes.forEach(note => {
        if (note.summary) {
          summaries[note._id] = note.summary;
        }
        if (note.coachAnalysis) {
          coachAnalyses[note._id] = note.coachAnalysis;
        }
        if (note.customAnalysis) { // Ensure this gets populated
          customAnalyses[note._id] = note.customAnalysis;
        }
      });
  
      setNoteSummaries(summaries);
      setNoteAnalyses(coachAnalyses);
      setCustomAnalyses(customAnalyses); // Set the custom analyses in the state
    } catch (error) {
      console.error('Failed to load client data:', error);
      if (error.response && error.response.status === 401) {

      } else {
        setErrorMessage('Failed to load client data.');
      }
    }
  };

  // Check Pro status for Coach Analysis gate
  useEffect(() => {
    const checkPlan = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/billing/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setIsPro(['pro','circle_member','circle_3','circle_5','circle_10'].includes(response.data.plan?.toLowerCase()));
      } catch (error) {
        console.error('Error checking plan:', error);
      }
    };
    checkPlan();
  }, []);

  useEffect(() => {
  if (!isRecording || isPaused) {
    setTranscriptionStatus('idle');
    return;
  }

  // Check every 5 seconds if transcription is still active
  const healthCheck = setInterval(() => {
    const timeSinceLastUpdate = Date.now() - lastTranscriptionTimeRef.current;
    
    // If no transcription for 30 seconds while recording, mark as hung
    if (timeSinceLastUpdate > 30000) {
      setTranscriptionStatus('hung');
      console.warn('Transcription appears to be hung - no activity for 30 seconds');
    } else {
      setTranscriptionStatus('active');
    }
  }, 5000);

  return () => clearInterval(healthCheck);
}, [isRecording, isPaused]);

  useEffect(() => {
    if (token) {
      const decodedToken = jwtDecode(token);
      setRemainingTime(decodedToken.exp - Date.now() / 1000);
    }
  }, [token]);

  useEffect(() => {
    if (noteTextareaRef.current) {
      noteTextareaRef.current.scrollTop = noteTextareaRef.current.scrollHeight;
    }
  }, [newSessionNote]);

  useEffect(() => {
    let timerId;

    if (remainingTime != null) {
      // Update the remaining time every second
      timerId = setInterval(() => {
        setRemainingTime((prevTime) => {
          const newTime = prevTime - 1;

          // Show session modal if time is less than 2 minutes (120 seconds)
          if (newTime <= 120 && !showSessionModal) {
            setShowSessionModal(true);
          }

          // If time is up, handle session timeout
          if (newTime <= 0) {
            clearInterval(timerId);
            handleSessionTimeout();
          }

          return newTime;
        });
      }, 1000);
    }

    return () => clearInterval(timerId);
  }, [remainingTime, showSessionModal]);

  // Browser-based transcription visibility change handler
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && isRecording && recognitionInstance) {
        console.warn('Page is hidden. Attempting to maintain recording.');
        recognitionInstance?.stop();
      } else if (document.visibilityState === 'visible' && isRecording && recognitionInstance) {
        console.info('Page is visible again. Restarting recognition.');
        handleStartBrowserRecording();
      }
    };
  
    document.addEventListener('visibilitychange', handleVisibilityChange);
  
    // Cleanup function to remove the event listener when the component unmounts
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isRecording, recognitionInstance]); // Dependencies to watch for changes

      useEffect(() => {
  const fetchUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
      if (res.data.externalAIUrl) setExternalAIUrl(res.data.externalAIUrl);
    } catch (err) {
      console.error('Failed to load user', err);
      // Fallback: try to extract from JWT
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const decoded = jwtDecode(token);
          setUser({
            firstName: decoded.firstName || decoded.name?.split(' ')[0] || '',
            lastName: decoded.lastName || decoded.name?.split(' ').slice(1).join(' ') || '',
          });
        }
      } catch (jwtErr) {
        console.error('JWT fallback failed', jwtErr);
      }
    }
  };
  fetchUser();
}, []);

  
  useEffect(() => {

const fetchCustomCriteria = async () => {
  const token = localStorage.getItem('token');
  try {
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings/custom-criteria`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setCustomCriteria(response.data.criteria || ''); // Store the custom criteria
  } catch (error) {
    console.error('Failed to fetch custom criteria:', error);
  }
};

const fetchUserPreferences = async () => {
  const token = localStorage.getItem('token');
  try {
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    setTonePreference(res.data.tonePreference || 'professional');
    setCustomPrompt(res.data.customPrompts || '');
  } catch (err) {
    console.error('Failed to fetch user preferences:', err);
  }
};


    // Fetch both data in parallel
fetchClientData();
fetchCustomCriteria();
fetchUserPreferences();

// Fetch AI templates
const fetchTemplates = async () => {
  try {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/openai/templates/${userId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setAiTemplates(res.data || []);
  } catch (err) {
    console.warn('Could not load templates:', err);
  }
};
fetchTemplates();
  }, [clientId, navigate]);

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    const clientId = clientData._id;
    if (!clientId) { setErrorMessage('Client ID is missing.'); return; }
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/clients/${clientId}`, {
        email: clientData.email,
        phone: clientData.phone,
        phone2: clientData.phone2 || '',
        company: clientData.company || '',
        title: clientData.title || '',
        website: clientData.website || '',
        address: clientData.address || '',
        clientNotes: clientData.clientNotes || '',
        sessionRate: parseFloat(clientData.sessionRate) || 0,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (response.status === 200) {
        setIsEditing(false);
        setNotification('Client updated successfully');
        setTimeout(() => setNotification(''), 3000);
      } else {
        setErrorMessage('Failed to update client.');
      }
    } catch (error) {
      console.error('Failed to save client data:', error);
      setErrorMessage('Failed to save client data.');
    }
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  // Modify `toggleSummary` to update `isViewingSummary` and manage button text correctly
  const toggleSummary = (noteId) => {
    if (selectedNote && selectedNote._id === noteId && isViewingSummary) {
      // If currently viewing summary, toggle back to the note
      setIsViewingSummary(false);
      setExpandedSummaries((prevState) => ({
        ...prevState,
        [noteId]: false, // Set the expanded state for this note to false
      }));
    } else {
      // Otherwise, set to viewing summary mode and deactivate others
      setIsViewingSummary(true);
      setIsViewingAnalysis(false);
      setIsViewingCustomAnalysis(false);
  
      setExpandedSummaries((prevState) => ({
        ...prevState,
        [noteId]: true, // Set the expanded state for this note to true
      }));
      setExpandedAnalyses({});
      setExpandedCustomAnalyses({});
      setSelectedNote(clientData.sessionNotes.find((note) => note._id === noteId)); // Ensure the selected note is set correctly
    }
  };

// Example of setting custom analysis after performing custom analysis
// Function to handle Custom Analysis
const handleCustomAnalysis = async (noteText, noteId) => {
  setLoadingCustomAnalyses((prevStates) => ({
    ...prevStates,
    [noteId]: true, // Set loading to true for the specific note's custom analysis
  }));

  try {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/openai/custom-analysis`, { notes: noteText, customCriteria });
    setCustomAnalyses((prevAnalyses) => ({
      ...prevAnalyses,
      [noteId]: response.data.analysis, // Store the analysis for the current note
    }));
  } catch (error) {
    console.error('Failed to perform custom analysis:', error);
  } finally {
    setLoadingCustomAnalyses((prevStates) => ({
      ...prevStates,
      [noteId]: false, // Set loading to false once the analysis is complete
    }));
  }
};

// AI Chat Assistant Functions
const handleToggleNoteForAI = (noteId) => {
  setSelectedNotesForAI((prev) => {
    if (prev.includes(noteId)) {
      return prev.filter((id) => id !== noteId);
    } else {
      return [...prev, noteId];
    }
  });
};

const handleSelectAllNotesForAI = () => {
  if (clientData?.sessionNotes) {
    const allNoteIds = clientData.sessionNotes.map((note) => note._id);
    setSelectedNotesForAI(allNoteIds);
  }
};

const handleClearSelectedNotes = () => {
  setSelectedNotesForAI([]);
};

const handleAIChatSubmit = async (e) => {
  e.preventDefault();
  
  if (!aiChatInput.trim()) return;
  
  const token = localStorage.getItem('token');
  
  // Gather selected notes content
  const selectedNotesContent = clientData?.sessionNotes
    ?.filter((note) => selectedNotesForAI.includes(note._id))
    ?.map((note) => ({
      date: new Date(note.timestamp).toLocaleDateString(),
      text: note.text,
      summary: noteSummaries[note._id] || null,
      coachAnalysis: noteAnalyses[note._id] || null,
    })) || [];

  // Add user message to chat history
  const userMessage = {
    role: 'user',
    content: aiChatInput,
    timestamp: new Date(),
  };
  
  setAiChatMessages((prev) => [...prev, userMessage]);
  setAiChatInput('');
  setAiChatLoading(true);

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/openai/ai-chat`,
      {
        prompt: aiChatInput,
        sessionNotes: selectedNotesContent,
        conversationHistory: aiChatMessages,
        clientName: `${clientData?.firstname} ${clientData?.lastname}`,
      },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    const assistantMessage = {
      role: 'assistant',
      content: response.data.response,
      timestamp: new Date(),
    };

    setAiChatMessages((prev) => [...prev, assistantMessage]);
    setAiChatResponse(response.data.response);
  } catch (error) {
    console.error('AI Chat error:', error);
    const errorMessage = {
      role: 'assistant',
      content: 'Sorry, I encountered an error processing your request. Please try again.',
      timestamp: new Date(),
      isError: true,
    };
    setAiChatMessages((prev) => [...prev, errorMessage]);
  } finally {
    setAiChatLoading(false);
  }
};

const handleSaveAIResponse = async () => {
  if (!aiChatResponse) return;
  
  const token = localStorage.getItem('token');
  const newNote = {
    text: `[AI Generated]\n\n${aiChatResponse}`,
    timestamp: new Date(),
  };

  try {
    await axios.post(
      `${process.env.REACT_APP_API_URL}/api/clients/${clientId}/session-notes`,
      newNote,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    
    // Refresh client data to get updated notes list
    await fetchClientData();
    
    setNotification('AI response saved as session note!');
    setTimeout(() => setNotification(''), 3000);
  } catch (error) {
    console.error('Failed to save AI response:', error);
    setErrorMessage('Failed to save AI response.');
  }
};

const handleCopyAIResponse = () => {
  if (aiChatResponse) {
    navigator.clipboard.writeText(aiChatResponse);
    setNotification('Response copied to clipboard!');
    setTimeout(() => setNotification(''), 3000);
  }
};

const handleClearAIChat = () => {
  setAiChatMessages([]);
  setAiChatResponse(null);
};

const handleExportAIChat = () => {
  const chatContent = aiChatMessages
    .map((msg) => `[${msg.role.toUpperCase()}] ${new Date(msg.timestamp).toLocaleString()}\n${msg.content}`)
    .join('\n\n---\n\n');
  
  const blob = new Blob([chatContent], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `AI_Chat_${clientData?.firstname}_${clientData?.lastname}_${new Date().toISOString().split('T')[0]}.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ChatGPT Launcher Functions
const openChatGPTPopup = () => {
  const width = 500;
  const height = 700;
  const left = window.screenX + window.outerWidth - width - 50;
  const top = window.screenY + 100;
  
  const popup = window.open(
    'https://chat.openai.com',
    'ChatGPT',
    `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
  );
  
  setChatGPTPopup(popup);
  setNotification('ChatGPT opened! Copy a transcript and paste it there.');
  setTimeout(() => setNotification(''), 3000);
};

const copyNoteForChatGPT = (noteText, noteId) => {
  const criteriaHeader = customCriteria
    ? `${customCriteria}\n\n---\n\n`
    : '';
  const summarySection = (noteSummaries[noteId] || clientData?.sessionNotes?.find(n => n._id === noteId)?.summary)
    ? `Summary:\n${noteSummaries[noteId] || clientData.sessionNotes.find(n => n._id === noteId).summary}\n\n---\n\n`
    : '';
  const formattedText = `${criteriaHeader}${summarySection}Transcript:\n\n${noteText}`;
  navigator.clipboard.writeText(formattedText);
  setCopiedNoteId(noteId);
  setNotification('Copied with summary + criteria. Paste into your AI tool.');
  setTimeout(() => { setNotification(''); setCopiedNoteId(null); }, 3000);
};

const copySummaryForChatGPT = (noteId) => {
  const summary = noteSummaries[noteId];
  if (summary) {
    navigator.clipboard.writeText(`Here's a session summary for context:\n\n${summary}`);
    setNotification('Summary copied!');
    setTimeout(() => setNotification(''), 3000);
  }
};

const copyAllSelectedForChatGPT = () => {
  const selectedNotes = clientData?.sessionNotes?.filter(note => selectedNotesForAI.includes(note._id));
  if (!selectedNotes?.length) {
    setNotification('No notes selected. Select notes first.');
    setTimeout(() => setNotification(''), 3000);
    return;
  }
  const criteriaHeader = customCriteria ? `${customCriteria}\n\n---\n\n` : '';
  const formattedText = selectedNotes.map((note, i) => {
    let text = `--- Session ${i + 1} (${new Date(note.timestamp).toLocaleDateString()}) ---\n${note.text}`;
    if (noteSummaries[note._id] || note.summary) text += `\n\n[Summary]: ${noteSummaries[note._id] || note.summary}`;
    return text;
  }).join('\n\n');
  navigator.clipboard.writeText(`${criteriaHeader}${formattedText}`);
  setNotification(`${selectedNotes.length} transcript(s) copied. Paste into your AI tool.`);
  setTimeout(() => setNotification(''), 3000);
};

const copyWithCustomPrompt = () => {
  const selectedNotes = clientData?.sessionNotes?.filter(note => selectedNotesForAI.includes(note._id));
  if (!selectedNotes?.length) {
    setNotification('No notes selected. Select notes first.');
    setTimeout(() => setNotification(''), 3000);
    return;
  }
  const notesText = selectedNotes.map((note, i) =>
    `--- Session ${i + 1} (${new Date(note.timestamp).toLocaleDateString()}) ---\n${note.text}`
  ).join('\n\n');
  const promptTemplate = customCriteria
    ? `${customCriteria}\n\nCLIENT: ${clientData?.firstName} ${clientData?.lastName}\n\nTRANSCRIPTS:\n${notesText}`
    : `Please analyze the following session transcripts for ${clientData?.firstName} ${clientData?.lastName}:\n\n${notesText}`;

  navigator.clipboard.writeText(promptTemplate);
  setNotification('Coaching prompt with transcripts copied!');
  setTimeout(() => setNotification(''), 3000);
};



  // Function to summarize a single note
const summarizeSingleNote = async (noteText, noteId) => {
  setLoadingSummaries((prevStates) => ({
    ...prevStates,
    [noteId]: true,
  }));

  try {
    const template = aiTemplates.find(t => t._id === selectedTemplate);
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/openai/summarize`, {
      notes: noteText,
      tonePreference,
      customPrompt: template?.promptTemplate || customPrompt
    });

    setNoteSummaries((prevSummaries) => ({
      ...prevSummaries,
      [noteId]: response.data.summary,
    }));
  } catch (error) {
    console.error('Failed to summarize session notes:', error);
    setErrorMessage('Failed to generate summary.');
  } finally {
    setLoadingSummaries((prevStates) => ({
      ...prevStates,
      [noteId]: false,
    }));
  }
};


const extractActionItems = async (noteText, noteId) => {
  setLoadingActions(prev => ({ ...prev, [noteId]: true }));
  try {
    const token = localStorage.getItem('token');
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/openai/extract-actions`,
      { text: noteText },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setActionItems(prev => ({ ...prev, [noteId]: res.data.actionItems }));
  } catch (err) {
    console.error('Failed to extract action items:', err);
  } finally {
    setLoadingActions(prev => ({ ...prev, [noteId]: false }));
  }
};

  const handleAddSessionNote = async () => {
    const token = localStorage.getItem('token');
    const newNote = {
      text: newSessionNote,
      timestamp: new Date(),
    };
  
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/clients/${clientId}/session-notes`,
        newNote,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
  
      console.log('✅ Transcription saved as session note.');
      console.log('Backend response:', response.data); // ✅ this belongs here
  
      const savedNote = response.data;
  
      // ✅ State updates
      setClientData((prevData) => ({
        ...prevData,
        sessionNotes: [...prevData.sessionNotes, savedNote],
      }));
  
      setNoteSummaries((prevSummaries) => ({
        ...prevSummaries,
        [savedNote._id]: '',
      }));
  
      setSelectedNote(savedNote);
      setNewSessionNote(''); // Clear the textarea
      setActiveNoteTab('view'); // Switch to view tab to show the saved note
      setNotification('✅ Session note saved successfully!');
      setTimeout(() => setNotification(''), 4000);
      setSavedModalMessage('Session note saved!');
      setShowSavedModal(true);
      setTimeout(() => {
        setShowSavedModal(false);
        if (user?.defaultSessionRate || clientData?.sessionRate) {
          const rate = clientData?.sessionRate || user?.defaultSessionRate;
          setBillingPromptData({
            clientId,
            sessionNoteId: savedNote._id,
            sessionTitle: savedNote.title || `Session ${new Date().toLocaleDateString()}`,
            durationMinutes: 0,
            rate,
          });
          setShowBillingPrompt(true);
        }
      }, 3000);
  
      // ✅ Safe to await here
      await fetchClientData();
    } catch (error) {
      console.error('❌ Failed to add session note:', error);
      setErrorMessage('Failed to add session note.');
    }
  };
  

// Save the summary
const handleSaveSummary = async (noteId) => {
  const token = localStorage.getItem('token');
  const summaryToSave = noteSummaries[noteId];

  setSavingSummaryId(noteId);
  try {
    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/api/clients/${clientId}/session-notes/${noteId}/summary`,
      { summary: summaryToSave },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setClientData(response.data);

    // Keep selectedNote in sync so the "already saved" comparison stays accurate
    const updatedNote = response.data.sessionNotes.find((n) => n._id === noteId);
    if (updatedNote) setSelectedNote(updatedNote);

    setNotification('Summary saved successfully!');
    setTimeout(() => setNotification(''), 3000);
  } catch (error) {
    console.error('Error saving summary:', error);
    setErrorMessage('Failed to save summary.');
  } finally {
    setSavingSummaryId(null);
  }
};

// Delete the summary with confirmation
const handleDeleteSummary = async (noteId) => {
  setConfirmModal({
    show: true,
    title: 'Delete Summary',
    message: 'Are you sure you want to delete this summary? This action cannot be undone.',
    onConfirm: async () => {
      setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
    const token = localStorage.getItem('token');
    try {
      // Remove summary for the note by sending a DELETE request to the server
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/clients/${clientId}/session-notes/${noteId}/delete-summary`,
        { summary: '' }, // Set summary to an empty string to "delete" it
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Update local state to remove the summary from UI
      setNoteSummaries((prevSummaries) => {
        const updatedSummaries = { ...prevSummaries };
        delete updatedSummaries[noteId]; // Remove the summary from the state
        return updatedSummaries;
      });

      // Update the client data in the session notes
      setClientData((prevData) => ({
        ...prevData,
        sessionNotes: prevData.sessionNotes.map((note) =>
          note._id === noteId ? { ...note, summary: '' } : note
        ),
      }));

      // Keep selectedNote in sync so the "already saved" comparison stays accurate
      setSelectedNote((prev) =>
        prev && prev._id === noteId ? { ...prev, summary: '' } : prev
      );

      console.log('Summary deleted successfully.');
    } catch (error) {
      console.error('Failed to delete the summary:', error);
      setErrorMessage('Failed to delete summary.');
    }
    }
  });
};

  const handleDeleteNote = async (noteId) => {
    const token = localStorage.getItem('token');
    setConfirmModal({
      show: true,
      title: 'Delete Session Note',
      message: 'Are you sure you want to delete this session note? This action cannot be undone.',
      onConfirm: async () => {
        setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
        try {
          await axios.delete(`${process.env.REACT_APP_API_URL}/api/clients/${clientId}/session-notes/${noteId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          setClientData((prevData) => ({
            ...prevData,
            sessionNotes: prevData.sessionNotes.filter((note) => note._id !== noteId),
          }));
          setSelectedNote(null);
        } catch (error) {
          console.error('Failed to delete session note:', error);
        }
      }
    });
  };

  const handleRenameNote = async (noteId, newTitle) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/clients/${clientId}/session-notes/${noteId}/title`,
        { title: newTitle.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update locally
      setClientData((prevData) => ({
        ...prevData,
        sessionNotes: prevData.sessionNotes.map((note) =>
          note._id === noteId ? { ...note, title: newTitle.trim() } : note
        ),
      }));
    } catch (error) {
      console.error('Failed to rename session note:', error);
    }
  };

  // Updated `handleSelectNote` to toggle session notes visibility
  const handleSelectNote = (note) => {
    if (selectedNote && selectedNote._id === note._id) {
      // If the note is already selected, deselect it to hide
      setSelectedNote(null);
      setIsViewingSummary(false); // Switch off summary mode when hiding the note
    } else {
      // Select and show the clicked note
      setSelectedNote(note);
      setIsViewingSummary(false); // Ensure summary mode is off when selecting a note
      setIsEditingNote(false);
    }
  };



  // Enable editing of a selected session note
  const handleEditNote = (note) => {
    setEditNoteText(note.text); // Set the current note text in the editor
    setSelectedNote(note); // Set the selected note
    setIsEditingNote(true); // Enable editing mode for the selected note
  };

  // Save the edited session note
  const handleSaveEditedNote = async () => {
    const token = localStorage.getItem('token'); // Get token from localStorage
  
    try {
      // Send PUT request to update the session note
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/clients/${clientId}/session-notes/${selectedNote._id}`, // Endpoint to update note
        { text: editNoteText }, // Send the updated note text
        {
          headers: { Authorization: `Bearer ${token}` }, // Attach token in headers
        }
      );
  
      // Update session notes in the state immediately after saving
      setClientData((prevData) => ({
        ...prevData,
        sessionNotes: prevData.sessionNotes.map((note) =>
          note._id === selectedNote._id ? response.data : note // Replace the edited note with the updated one
        ),
      }));
      setIsEditingNote(false); // Exit editing mode after saving
      setSelectedNote(response.data); // Update the selected note with the saved one
    } catch (error) {
      setErrorMessage('Failed to update session note.');
    }
  };



  // Handle session expiration
  const handleSessionTimeout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  // Handle token refresh or stay logged in
  const handleStayLoggedIn = async () => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/refresh-token`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.token) {
        localStorage.setItem('token', response.data.token); // Update token in localStorage
        setRemainingTime(jwtDecode(response.data.token).exp - Date.now() / 1000); // Update remaining time
        setShowSessionModal(false); // Close the modal
      }
    } catch (error) {
      console.error('Failed to refresh session:', error);
      handleSessionTimeout();
    }
  };

  // Function to handle Coach Analysis
  const handleCoachAnalysis = async (noteText, noteId) => {
    setLoadingCoachAnalyses((prevStates) => ({
      ...prevStates,
      [noteId]: true, // Set loading to true for the specific note
    }));

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/openai/coach-analysis`, { notes: noteText });
      setNoteAnalyses((prevAnalyses) => ({
        ...prevAnalyses,
        [noteId]: response.data.analysis, // Store the analysis for the current note
      }));
    } catch (error) {
      console.error('Failed to analyze the session note:', error);
      setErrorMessage('Failed to perform analysis.');
    } finally {
      setLoadingCoachAnalyses((prevStates) => ({
        ...prevStates,
        [noteId]: false, // Set loading to false once the analysis is complete
      }));
    }
  };

// Save the coach analysis
const handleSaveCoachAnalysis = async (noteId) => {
  const token = localStorage.getItem('token');
  const analysisToSave = noteAnalyses[noteId]; // Get the coach analysis for the specific note

  try {
    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/api/clients/${clientId}/session-notes/${noteId}/coach-analysis`,
      { coachAnalysis: analysisToSave },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // Update the client data with the new coach analysis
    setClientData(response.data);

    // Show notification
    setNotification('Coach analysis saved successfully!');

    // Hide notification after 3 seconds
    setTimeout(() => setNotification(''), 3000);
  } catch (error) {
    console.error('Error saving coach analysis:', error);
    setErrorMessage('Failed to save coach analysis.');
  }
};

  // Delete the coach analysis with confirmation
  const handleDeleteCoachAnalysis = async (noteId) => {
    setConfirmModal({
      show: true,
      title: 'Delete Coach Analysis',
      message: 'Are you sure you want to delete this coach analysis? This action cannot be undone.',
      onConfirm: async () => {
        setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
        const token = localStorage.getItem('token');
        try {
          await axios.put(
            `${process.env.REACT_APP_API_URL}/api/clients/${clientId}/session-notes/${noteId}/coach-analysis`,
            { coachAnalysis: '' },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          setNoteAnalyses((prevAnalyses) => {
            const updatedAnalyses = { ...prevAnalyses };
            delete updatedAnalyses[noteId];
            return updatedAnalyses;
          });

          console.log('Coach analysis deleted successfully.');
        } catch (error) {
          console.error('Failed to delete coach analysis:', error);
        }
      }
    });
  };

// Function to toggle the visibility of the Coach Analysis
const toggleAnalysis = (noteId) => {
  if (selectedNote && selectedNote._id === noteId && isViewingAnalysis) {
    // If currently viewing analysis, toggle back to the note
    setIsViewingAnalysis(false);
    setExpandedAnalyses((prevState) => ({
      ...prevState,
      [noteId]: false, // Set the expanded state for this note to false
    }));
  } else {
    // Otherwise, set to viewing analysis mode and deactivate others
    setIsViewingAnalysis(true);
    setIsViewingSummary(false);
    setIsViewingCustomAnalysis(false);

    setExpandedAnalyses((prevState) => ({
      ...prevState,
      [noteId]: true, // Set the expanded state for this note to true
    }));
    setExpandedSummaries({});
    setExpandedCustomAnalyses({});
    setSelectedNote(clientData.sessionNotes.find((note) => note._id === noteId)); // Ensure the selected note is set correctly
  }
};

// ==================== BROWSER-BASED TRANSCRIPTION ====================
const handleStartBrowserRecording = () => {
  if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
    alert('Your browser does not support speech recognition. Please use a supported browser like Google Chrome.');
    console.error('Browser does not support Speech Recognition API');
    return;
  }

  // Remove this check - it prevents resume from working
  // if (isRecording) {
  //   console.warn('Recording is already in progress.');
  //   return;
  // }

  // Initialize microphone stream
  navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: true } })
  .then((stream) => {
    micStream.current = stream;
    console.log('Microphone stream initialized for browser transcription.');
  })
  .catch((error) => {
    console.error('Microphone access error:', error);
  });

  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  const recognition = new SpeechRecognition();
  recognition.continuous = true;
  recognition.interimResults = true;
  recognition.lang = 'en-US';

recognition.onresult = (event) => {
  lastTranscriptionTimeRef.current = Date.now(); // Track activity
  let interimTranscription = '';
  for (let i = event.resultIndex; i < event.results.length; i++) {
    if (event.results[i].isFinal) {
      setTranscription((prev) => prev + event.results[i][0].transcript + ' ');
      setNewSessionNote((prev) => prev + event.results[i][0].transcript + ' ');
      
      if (noteTextareaRef.current) {
        noteTextareaRef.current.scrollTop = noteTextareaRef.current.scrollHeight;
      }
    } else {
      interimTranscription += event.results[i][0].transcript;
    }
  }
};

  recognition.onerror = (event) => {
    console.error('Speech recognition error:', event.error);
    setIsRecording(false);
    if (['network', 'no-speech', 'audio-capture'].includes(event.error)) {
      setTimeout(() => handleStartBrowserRecording(), 4000);
    }
  };

  recognition.onend = () => {
    if (isRecording && !isPaused) {
      setTimeout(() => handleStartBrowserRecording(), 4000);
    }
  };

  recognition.start();
  setRecognitionInstance(recognition);
  setIsRecording(true);
  setIsPaused(false); // Ensure isPaused is set to false when starting
  console.log('Browser-based recording started');
};

const handleStopBrowserRecording = async () => {
  // Stop speech recognition
  if (recognitionInstance) {
    recognitionInstance.stop();
    recognitionInstance.onend = null;
    setRecognitionInstance(null);
  }

  // Stop the microphone stream
  if (micStream.current) {
    micStream.current.getTracks().forEach((track) => {
      track.stop();
    });
    micStream.current = null;
  }

  // Update recording state
  setIsRecording(false);
  console.log('Browser recording stopped and resources cleared.');

  // Auto-save transcription if there's content
  if (newSessionNote.trim().length > 0) {
    console.log('Auto-saving browser transcription...');
    await saveTranscriptionAsNote();
    
    // Show notification
    setNotification('Session note saved successfully!');
    setTimeout(() => setNotification(''), 3000);
    
    // Clear textarea for new session
    setNewSessionNote('');
    
    // Refresh client data to show the new note in the list
    await fetchClientData();
  }
};

// ==================== SERVER-SIDE TRANSCRIPTION ====================
const handleStartServerRecording = async () => {
    await enableWakeLock();
  try {
    console.log('Starting server-side transcription recording...');
    
    const stream = await navigator.mediaDevices.getUserMedia({ 
      audio: {
        sampleRate: 16000,
        channelCount: 1,
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: true
      } 
    });

    // Check supported MIME types in order of preference
    const mimeTypes = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/wav',
      'audio/mp4',
      '' // Let browser choose
    ];

    let selectedMimeType = '';
    for (const mimeType of mimeTypes) {
      if (mimeType === '' || MediaRecorder.isTypeSupported(mimeType)) {
        selectedMimeType = mimeType;
        console.log(`Using MIME type: ${mimeType || 'browser default'}`);
        break;
      }
    }

    let currentRecorder = new MediaRecorder(stream, {
      mimeType: selectedMimeType,
      audioBitsPerSecond: 64000
    });

    // Store chunks temporarily
    let tempAudioChunks = [];

    currentRecorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        console.log('Audio chunk received:', event.data.size, 'bytes');
        tempAudioChunks.push(event.data);
      }
    };

    currentRecorder.onstop = () => {
      console.log('MediaRecorder stopped, processing final chunks');
      // Only process if still actively recording (not user-initiated stop)
      if (recordingStateRef.current && tempAudioChunks.length > 0) {
        processChunksAsComplete(tempAudioChunks, currentRecorder.mimeType);
        tempAudioChunks = [];
      } else {
        console.log('Recording stopped by user, skipping final chunk processing');
        tempAudioChunks = [];
      }
    };

    currentRecorder.onerror = (event) => {
      console.error('MediaRecorder error:', event.error);
    };

    setMediaRecorder(currentRecorder);
    setIsRecording(true);
    recordingStateRef.current = true; // Set the ref
    recordingStartTimeRef.current = Date.now(); // track start time for billing
    
    // Start recording continuously (no time slicing)
    currentRecorder.start();

    // Set up interval to stop/restart recorder every 10 seconds
    recordingIntervalRef.current = {
      recordingInterval: setInterval(() => {
        if (currentRecorder && currentRecorder.state === 'recording') {
          console.log('Stopping recorder to process complete audio segment...');
          currentRecorder.stop();
          
          // Create new recorder for next segment
          setTimeout(() => {
            // Use ref instead of state to avoid stale closure
            if (recordingStateRef.current && recordingIntervalRef.current) {
              console.log('Creating new recorder for next segment...');
              
              // Create new recorder
              currentRecorder = new MediaRecorder(stream, {
                mimeType: selectedMimeType,
                audioBitsPerSecond: 64000
              });

              tempAudioChunks = []; // Reset chunks

              currentRecorder.ondataavailable = (event) => {
                if (event.data && event.data.size > 0) {
                  console.log('Audio chunk received:', event.data.size, 'bytes');
                  tempAudioChunks.push(event.data);
                }
              };

              currentRecorder.onstop = () => {
                console.log('MediaRecorder stopped, processing final chunks');
                // Only process if still actively recording (not user-initiated stop)
                if (recordingStateRef.current && tempAudioChunks.length > 0) {
                  processChunksAsComplete(tempAudioChunks, currentRecorder.mimeType);
                  tempAudioChunks = [];
                } else {
                  console.log('Recording stopped by user, skipping final chunk processing');
                  tempAudioChunks = [];
                }
              };

              currentRecorder.onerror = (event) => {
                console.error('MediaRecorder error:', event.error);
              };

              setMediaRecorder(currentRecorder);
              currentRecorder.start();
              console.log('Started new recording segment');
            } else {
              console.log('Recording stopped, not creating new segment');
            }
          }, 100);
        }
      }, 10000), // 10 second intervals
      
      autoSaveInterval: setInterval(() => {
        if (recordingStateRef.current && newSessionNote.trim().length > 0) {
          saveTranscriptionAsNote();
        }
      }, 600000)
    };

    console.log('Server-side recording started with 10-second complete segments');
    
  } catch (error) {
    console.error('Error starting recording:', error);
    setIsRecording(false);
    recordingStateRef.current = false;
  }
};

const processChunksAsComplete = (chunks, mimeType) => {
  if (chunks.length === 0) {
    console.log('No chunks to process');
    return;
  }

  console.log(`Processing ${chunks.length} chunks as complete audio segment`);
  
  // Create a complete audio blob from all chunks
  const audioBlob = new Blob(chunks, { type: mimeType || 'audio/webm' });
  
  if (audioBlob.size > 1000) { // Only process substantial audio
    console.log(`Processing complete audio segment: ${audioBlob.size} bytes`);
    sendAudioToServer(audioBlob);
  } else {
    console.log(`Skipping small audio segment: ${audioBlob.size} bytes`);
  }
};

const sendAudioToServer = async (audioBlob) => {
  try {
    console.log('Sending audio to server for transcription...', audioBlob.size, 'bytes');
    
    const formData = new FormData();
    
    // Create proper filename based on blob type
    let filename = 'recording.webm';
    if (audioBlob.type.includes('ogg')) {
      filename = 'recording.ogg';
    } else if (audioBlob.type.includes('wav')) {
      filename = 'recording.wav';
    } else if (audioBlob.type.includes('mp4')) {
      filename = 'recording.m4a';
    }
    
    formData.append('audio', audioBlob, filename);
    formData.append('clientId', clientId); // ← add this

    // Send diarization params if enabled
    if (diarizeEnabled) {
      formData.append('diarize', 'true');
      const coachName = [user?.firstname || user?.firstName, user?.lastname || user?.lastName].filter(Boolean).join(' ') || 'Coach';
      const clientName = [clientData?.firstname || clientData?.firstName, clientData?.lastname || clientData?.lastName].filter(Boolean).join(' ') || 'Client';
      formData.append('coachName', coachName);
      formData.append('clientName', clientName);
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/transcribe`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        timeout: 60000,
      }
    );

    console.log('Server response:', response.data);

if (response.data && response.data.text && response.data.text.trim()) {
  lastTranscriptionTimeRef.current = Date.now(); // Track activity
  const transcribedText = response.data.text.trim();
  console.log('Real-time transcription chunk:', transcribedText);
      
      const formattedText = transcribedText.endsWith('.') ? 
        transcribedText + ' ' : 
        transcribedText + '. ';
      
      setNewSessionNote(prev => {
        const updated = prev + formattedText;
        return updated;
      });
      
      setTranscription(prev => prev + formattedText);
      
      if (noteTextareaRef.current) {
        noteTextareaRef.current.scrollTop = noteTextareaRef.current.scrollHeight;
      }
      
      lastTranscriptionTime = Date.now();
    } else {
      console.log('No transcription text returned (silence or processing error)');
      if (response.data.error) {
        console.error('Transcription service error:', response.data.error);
      }
    }

  } catch (error) {
    console.error('Error sending audio to server:', error);
    
    if (error.code === 'ECONNREFUSED') {
      console.error('Connection refused - transcription service may be down');
      setErrorMessage('Transcription service unavailable');
    } else if (error.code === 'ETIMEDOUT') {
      console.error('Request timeout - audio may be too large or service slow');
      setErrorMessage('Transcription request timed out');
    } else if (error.response) {
      console.error('Server error:', error.response.status, error.response.data);
      setErrorMessage(`Transcription error: ${error.response.data.error || 'Unknown server error'}`);
    } else {
      console.error('Network or other error:', error.message);
      setErrorMessage('Network error during transcription');
    }
  }
};

const handleStopServerRecording = async () => {
    await disableWakeLock();
    setIsStealthMode(false);
  console.log('Stopping server-side recording...');
  
  recordingStateRef.current = false;
  
  if (recordingIntervalRef.current?.recordingInterval) {
    clearInterval(recordingIntervalRef.current.recordingInterval);
  }
  
  if (recordingIntervalRef.current?.autoSaveInterval) {
    clearInterval(recordingIntervalRef.current.autoSaveInterval);
  }
  
  recordingIntervalRef.current = null;

  if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    mediaRecorder.stop();
    mediaRecorder.stream.getTracks().forEach(track => track.stop());
  }

  setIsRecording(false);
  setMediaRecorder(null);
  console.log('Server-side recording stopped');

  // Auto-save transcription if there's content
  if (newSessionNote.trim().length > 0) {
    console.log('Auto-saving server transcription...');
    const durationMinutes = recordingStartTimeRef.current
      ? Math.round((Date.now() - recordingStartTimeRef.current) / 60000)
      : 0;
    recordingStartTimeRef.current = null;
    await saveTranscriptionAsNote(durationMinutes);
    
    // Show notification
    setNotification('Session note saved successfully!');
    setTimeout(() => setNotification(''), 3000);
    
    // Clear textarea for new session
    setNewSessionNote('');
    
    // Refresh client data to show the new note in the list
    await fetchClientData();
  }
};

const handlePauseBrowserRecording = () => {
  if (recognitionInstance) {
    recognitionInstance.stop();
    recognitionInstance.onend = null;
  }
  setIsPaused(true);
  console.log('Browser recording paused');
};

const handleResumeBrowserRecording = () => {
  setIsPaused(false);
  handleStartBrowserRecording();
  console.log('Browser recording resumed');
};

const handlePauseServerRecording = () => {
  setIsPaused(true);
  recordingStateRef.current = false;
  
  if (mediaRecorder && mediaRecorder.state === 'recording') {
    mediaRecorder.pause();
  }
  
  console.log('Server recording paused');
};

const handleResumeServerRecording = () => {
  setIsPaused(false);
  recordingStateRef.current = true;
  
  if (mediaRecorder && mediaRecorder.state === 'paused') {
    mediaRecorder.resume();
  }
  
  console.log('Server recording resumed');
};

// Function to handle saving custom analysis
const handleSaveCustomAnalysis = async (noteId) => {
  const token = localStorage.getItem('token');
  const customAnalysisText = customAnalyses[noteId]; // Get the custom analysis text from state

  try {
    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/api/clients/${clientId}/session-notes/${noteId}/custom-analysis`,
      { customAnalysis: customAnalysisText },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
   // Show notification
   setNotification('Custom analysis saved successfully!');
       // Hide notification after 3 seconds
       setTimeout(() => setNotification(''), 3000);
  } catch (error) {
    console.error('Failed to save custom analysis:', error);
  }
};

// Delete custom analysis
const handleDeleteCustomAnalysis = async (noteId) => {
  const token = localStorage.getItem('token');

  try {
    await axios.delete(`${process.env.REACT_APP_API_URL}/api/clients/${clientId}/session-notes/${noteId}/custom-analysis`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    setCustomAnalyses(prevState => {
      const newState = { ...prevState };
      delete newState[noteId];
      return newState;
    });

    alert('Custom analysis deleted successfully!');
  } catch (error) {
    console.error('Failed to delete custom analysis:', error);
  }
};

// Utility function to convert URLs to clickable links
const makeLinksClickable = (text) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text.replace(urlRegex, (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`);
};

const handleCreateBillingRecord = async () => {
  if (!billingPromptData) return;
  try {
    const token = localStorage.getItem('token');
    await axios.post(
      `${process.env.REACT_APP_API_URL}/api/billing-records`,
      {
        clientId: billingPromptData.clientId,
        sessionNoteId: billingPromptData.sessionNoteId,
        sessionTitle: billingPromptData.sessionTitle,
        durationMinutes: billingPromptData.durationMinutes,
        rate: billingPromptData.rate,
        date: new Date().toISOString(),
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setNotification('💳 Billing record created');
    setTimeout(() => setNotification(''), 2500);
  } catch (err) {
    console.error('[Billing] Failed to create record:', err);
  } finally {
    setShowBillingPrompt(false);
    setBillingPromptData(null);
  }
};

const handleSendTranscript = async (noteId) => {
  setSendingTranscript(true);
  try {
    const token = localStorage.getItem('token');
    await axios.post(
      `${process.env.REACT_APP_API_URL}/api/clients/${clientId}/session-notes/${noteId}/send-transcript`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setSendTranscriptModal({ show: false, noteId: null });
    setNotification(`📧 Transcript sent to ${clientData?.firstName}`);
    setTimeout(() => setNotification(''), 4000);
  } catch (err) {
    console.error('Failed to send transcript:', err);
    setNotification('Failed to send transcript. Please try again.');
    setTimeout(() => setNotification(''), 3000);
  } finally {
    setSendingTranscript(false);
  }
};

const handleSaveExternalAI = async () => {
  setSavingExternalAI(true);
  try {
    const token = localStorage.getItem('token');
    await axios.put(`${process.env.REACT_APP_API_URL}/api/users/external-ai`,
      { externalAIUrl },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setNotification('AI tool URL saved');
    setTimeout(() => setNotification(''), 2000);
  } catch (err) {
    console.error('Failed to save external AI URL:', err);
  } finally {
    setSavingExternalAI(false);
  }
};

const handleNavigateToSettings = (hash = '') => {
  navigate(`/settings${hash}`);
};

const filteredSessionNotes = clientData?.sessionNotes?.filter(note => {
  if (!searchQuery.trim()) return true;
  const query = searchQuery.toLowerCase();
  return (
    note.text?.toLowerCase().includes(query) ||
    note.title?.toLowerCase().includes(query) ||
    note.summary?.toLowerCase().includes(query)
  );
}) || [];

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text)
    .then(() => alert('Text copied to clipboard!'))
    .catch((err) => console.error('Failed to copy text: ', err));
};

const formatPhoneNumber = (value) => {
  if (!value) return value;

  // Remove all non-numeric characters
  const phoneNumber = value.replace(/[^\d]/g, '');

  // Format phone number based on length
  const phoneNumberLength = phoneNumber.length;

  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

const handleChange = (e) => {
  const { name, value } = e.target;

  // If the input is the phone field, apply the formatPhoneNumber function
  if (name === 'phone') {
    const formattedPhone = formatPhoneNumber(value);
    setClientData({
      ...clientData,
      phone: formattedPhone, // Set the formatted phone value
    });
  } else {
    setClientData({
      ...clientData,
      [name]: value, // Update other fields as usual
    });
  }
};

const saveTranscriptionAsNote = async (durationMinutes = 0) => {
  const token = localStorage.getItem('token');
  
  setProcessingTranscript(true);
  setNotification('✨ Processing transcript...');

  // Format transcript before saving
  let formattedText = newSessionNote;
  try {
    const formatRes = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/openai/format-transcript`,
      { text: newSessionNote },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (formatRes.data.formatted) {
      formattedText = formatRes.data.formatted;
    }
  } catch (err) {
    console.warn('Formatting skipped:', err);
  }

  const newNote = {
    text: formattedText,
    timestamp: new Date(),
  };

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/clients/${clientId}/session-notes`,
      newNote,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    const savedNote = response.data;

    // Auto-generate title
    setNotification('✨ Generating title...');
    try {
      const titleRes = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/openai/generate-title`,
        { text: formattedText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (titleRes.data.title) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/clients/${clientId}/session-notes/${savedNote._id}/title`,
          { title: titleRes.data.title },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        savedNote.title = titleRes.data.title;
      }
    } catch (err) {
      console.warn('Auto-title generation failed:', err);
    }

    // Update state with the new note
    setClientData((prevData) => ({
      ...prevData,
      sessionNotes: [...prevData.sessionNotes, savedNote],
    }));

    setSelectedNote(savedNote);
    setNewSessionNote('');
    setActiveNoteTab('view');

    // Show confirmation notification
    setProcessingTranscript(false);
    setNotification('✅ Transcription saved as session note!');
    setTimeout(() => setNotification(''), 4000);
    setSavedModalMessage('Transcription saved as session note!');
    setShowSavedModal(true);
    setTimeout(() => {
      setShowSavedModal(false);
      if (user?.defaultSessionRate || clientData?.sessionRate) {
        const rate = clientData?.sessionRate || user?.defaultSessionRate;
        setBillingPromptData({
          clientId,
          sessionNoteId: savedNote._id,
          sessionTitle: savedNote.title || `Session ${new Date().toLocaleDateString()}`,
          durationMinutes,
          rate,
        });
        setShowBillingPrompt(true);
      }
    }, 3000);

    console.log('✅ Transcription saved as session note.');
  } catch (error) {
    console.error('❌ Failed to auto-save transcription:', error);
    setProcessingTranscript(false);
    setNotification('❌ Failed to save transcription. Please try again.');
    setTimeout(() => setNotification(''), 4000);
  }
};


    // Function to refresh the token and keep the user logged in
    const refreshSession = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/refresh-token`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        if (data.token) {
          localStorage.setItem('token', data.token); // Update token in localStorage
          setShowSessionModal(false); // Close the modal
          setRemainingTime(jwtDecode(data.token).exp - Date.now() / 1000); // Update remaining time
          alert('Session refreshed successfully!');
        }
      } catch (error) {
        console.error('Failed to refresh session:', error);
        handleLogout(); // Log out if token refresh fails
      }
    };

    const handleLogout = useCallback(() => {
      setConfirmModal({
        show: true,
        title: 'Log Out',
        message: 'Are you sure you want to log out?',
        onConfirm: () => {
          setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
          localStorage.removeItem('token');
          navigate('/login');
        }
      });
    }, [navigate]);

    const toggleNoteExpansion = (noteId) => {
      setExpandedNotes((prevState) => ({
        ...prevState,
        [noteId]: !prevState[noteId],
      }));
    };
  
// Function to toggle the collapsible analysis section for a specific note
const toggleAnalysisSection = (noteId) => {
  setExpandedSections((prevState) => ({
    ...prevState,
    [noteId]: !prevState[noteId], // Toggle the expanded state for the specific note
  }));
};

// Combined recording controls that show both options
// Combined recording controls that show both options
const renderRecordingControls = () => (
  <div className="recording-controls">
    <div className="transcription-options">
      <h4>Start Transcription:</h4>
      
      {!isRecording ? (
        <div className="recording-buttons">
          {/* Browser Transcription hidden - server-side is more robust
          <button 
            onClick={() => requireConsent(handleStartBrowserRecording)}
            className="start-recording-btn browser-transcription"
            title="Uses your browser's built-in speech recognition (faster, works offline)"
          >
            🎙️ Browser Transcription
          </button>
          */}
          
          <button 
            onClick={() => handleStartRecordingWithReminder(handleStartServerRecording)}
            className="start-recording-btn server-transcription"
            title="Sends audio to server for AI-powered transcription"
          >
            🎙️ Start Recording
          </button>
        </div>
      ) : (
        <div className="recording-active">
          {!isPaused ? (
            <>
              <button 
                onClick={recognitionInstance ? handlePauseBrowserRecording : handlePauseServerRecording}
                className="pause-recording-btn"
              >
                ⏸️ Pause Recording
              </button>
              <button 
                onClick={recognitionInstance ? handleStopBrowserRecording : handleStopServerRecording}
                className="stop-recording-btn"
              >
                ⏹️ Stop Recording
              </button>
            </>
          ) : (
            <>
              <button 
                onClick={recognitionInstance ? handleResumeBrowserRecording : handleResumeServerRecording}
                className="resume-recording-btn"
              >
                ▶️ Resume Recording
              </button>
              <button 
                onClick={recognitionInstance ? handleStopBrowserRecording : handleStopServerRecording}
                className="stop-recording-btn"
              >
                ⏹️ Stop Recording
              </button>
            </>
          )}
          <p className="recording-indicator">
            {isPaused ? '⏸️ Paused' : '🔴 Recording...'}
          </p>
          
          {/* Stealth Mode Button — mobile only */}
          {isNative && (
            <button
              onClick={toggleStealthMode}
              className={`stealth-mode-btn ${isStealthMode ? 'stealth-active' : ''}`}
              title="Darken screen while recording continues"
            >
              {isStealthMode ? '👁️ Show Screen' : '🌑 Stealth Mode'}
            </button>
          )}
          
          {/* Transcription Status Indicator */}
          {!isPaused && (
            <div className={`transcription-status ${transcriptionStatus}`}>
              {transcriptionStatus === 'active' && (
                <span className="status-active">✓ Actively Listening</span>
              )}
              {transcriptionStatus === 'hung' && (
                <span className="status-hung">⚠️ No transcription detected - Check microphone or speak louder</span>
              )}
            </div>
          )}
        </div>
      )}
      
      <div className="transcription-info">
        <small>
          <strong>AI-Powered Transcription</strong> — Accurate, real-time speech-to-text
        </small>
        
        {/* Diarization Toggle */}
        <label className="diarize-toggle" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={diarizeEnabled}
            onChange={(e) => setDiarizeEnabled(e.target.checked)}
            disabled={isRecording}
            style={{ width: '16px', height: '16px', accentColor: 'var(--primary)' }}
          />
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            🗣️ Speaker Identification {diarizeEnabled ? '(On)' : '(Off)'}
          </span>
          {diarizeEnabled && (
            <span style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>
              — {[user?.firstname || user?.firstName, user?.lastname || user?.lastName].filter(Boolean).join(' ') || 'Coach'} & {[clientData?.firstname || clientData?.firstName, clientData?.lastname || clientData?.lastName].filter(Boolean).join(' ') || 'Client'}
            </span>
          )}
        </label>
      </div>
    </div>
  </div>
);

return (
  <div className="cd-page">

    {/* Stealth overlay */}
    {isStealthMode && (
      <div className="stealth-overlay" onClick={toggleStealthMode} title="Tap to reveal">
        <div className="stealth-pulse" />
      </div>
    )}

    {clientData ? (
      <>
        {/* ── Header ── */}
        <div className="cd-header">
          <button className="cd-back-btn" onClick={() => navigate('/clients')}>
            ← Clients
          </button>
          <div className="cd-header-main">
            <div className="cd-avatar">
              {clientData.firstName?.[0]}{clientData.lastName?.[0]}
            </div>
            <div className="cd-header-info">
              <h1 className="cd-client-name">
                {clientData.firstName} {clientData.lastName}
              </h1>
              {(clientData.title || clientData.company) && (
                <p className="cd-client-sub">
                  {[clientData.title, clientData.company].filter(Boolean).join(' · ')}
                </p>
              )}
            </div>
            <div className="cd-header-actions">
              <button className="cd-pill-btn" onClick={() => handleNavigateToSettings('#ai-settings')}>
                ⚙ Analysis criteria
              </button>
              <button className="cd-pill-btn" onClick={() => handleNavigateToSettings('#ai-settings')}>
                ✦ AI personalization
              </button>
              <button className="cd-pill-btn cd-pill-accent" onClick={handleEditToggle}>
                ✎ {isEditing ? 'Cancel edit' : 'Edit client'}
              </button>
            </div>
          </div>
        </div>

        {errorMessage && <div className="cd-error">{errorMessage}</div>}
        {notification && <div className="cd-notification">{notification}</div>}

{/* ── Client Info Card ── */}
        <div className="cd-card">
          <p className="cd-card-label" onClick={() => setIsClientInfoExpanded(!isClientInfoExpanded)} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Client information
            <span>{isClientInfoExpanded ? '▼' : '▶'}</span>
          </p>
          {isClientInfoExpanded && (
          <div className="cd-info-grid">
            {[
              { label: 'Email', name: 'email', type: 'email' },
              { label: 'Phone', name: 'phone', type: 'tel' },
              { label: 'Secondary phone', name: 'phone2', type: 'tel' },
              { label: 'Company', name: 'company', type: 'text' },
              { label: 'Title', name: 'title', type: 'text' },
              { label: 'Website', name: 'website', type: 'url' },
              { label: 'Address', name: 'address', type: 'text' },
              { label: 'Session rate ($)', name: 'sessionRate', type: 'number' },
            ].map(({ label, name, type }) => (
              <div key={name} className="cd-info-field">
                <label>{label}</label>
                {isEditing ? (
                  <input
                    type={type}
                    name={name}
                    value={clientData[name] || ''}
                    onChange={handleChange}
                    placeholder={label}
                  />
                ) : (
                  <p className={!clientData[name] ? 'cd-empty' : ''}>
                    {name === 'website' && clientData[name] ? (
                      <a
                        href={clientData[name].startsWith('http') ? clientData[name] : `https://${clientData[name]}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {clientData[name]}
                      </a>
                    ) : name === 'sessionRate' ? (
                      clientData[name] > 0 ? `$${parseFloat(clientData[name]).toFixed(2)} / session` : <span className="cd-empty">Uses default rate</span>
                    ) : (
                      clientData[name] || 'Not set'
                    )}
                  </p>
                )}
              </div>
            ))}
            <div className="cd-info-field cd-info-notes">
              <label>Notes</label>
              {isEditing ? (
                <textarea
                  name="clientNotes"
                  value={clientData.clientNotes || ''}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Notes about this client..."
                />
              ) : (
                <p className={!clientData.clientNotes ? 'cd-empty' : ''}>{clientData.clientNotes || 'None'}</p>
              )}
            </div>
          </div>
          )}
          {isEditing && (
            <div className="cd-edit-actions">
              <button className="cd-save-btn" onClick={handleSave}>Save changes</button>
              <button className="cd-cancel-btn" onClick={() => setIsEditing(false)}>Cancel</button>
            </div>
          )}

          {/* ── Transcription consent reminder ── */}
<div style={{
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  marginTop: '1rem', paddingTop: '1rem',
  borderTop: '0.5px solid rgba(27,43,75,0.08)',
}}>
  <div>
    <p style={{ fontSize: '13px', fontWeight: 600, color: '#1B2B4B', margin: '0 0 2px' }}>
      Transcription consent reminder
    </p>
    <p style={{ fontSize: '12px', color: '#8898AA', margin: 0 }}>
      {consentReminderEnabled
        ? 'Prompt shown before each recording'
        : 'Reminder off — recording starts immediately'}
    </p>
  </div>
 <div
    onClick={async () => {
      const newVal = !consentReminderEnabled;
      setConsentReminderEnabled(newVal);
      try {
        await axios.patch(
          `${process.env.REACT_APP_API_URL}/api/consent/client/${clientId}/reminder`,
          { reminderEnabled: newVal },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
      } catch (e) {
        console.warn('Could not save reminder preference');
      }
    }}
    style={{
      width: '44px', height: '26px', borderRadius: '13px',
      background: consentReminderEnabled ? '#E8654A' : '#E9ECEF',
      position: 'relative', cursor: 'pointer', flexShrink: 0,
      transition: 'background 0.2s ease',
    }}
  >
    <div style={{
      position: 'absolute',
      top: '3px',
      left: consentReminderEnabled ? '21px' : '3px',
      width: '20px', height: '20px', borderRadius: '50%',
      background: '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      transition: 'left 0.2s ease',
    }} />
  </div>
</div>

{/* Send consent link — OUTSIDE the toggle row, after it */}
{consentReminderEnabled && (
  <div style={{ marginTop: '12px' }}>
    <button
      onClick={async () => {
        try {
          const res = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/consent/client/${clientId}/send`,
            {},
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
          );
          await navigator.clipboard.writeText(res.data.consentUrl);
          setNotification(`📋 Consent link copied — send it to ${clientData?.firstName}`);
          setTimeout(() => setNotification(''), 4000);
        } catch (e) {
          setNotification('Could not generate consent link');
          setTimeout(() => setNotification(''), 3000);
        }
      }}
      style={{
        width: '100%',
        padding: '11px',
        background: 'rgba(27,43,75,0.06)',
        color: '#1B2B4B',
        border: '0.5px solid rgba(27,43,75,0.15)',
        borderRadius: '10px',
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      🔗 Send consent link to {clientData?.firstName}
    </button>
  </div>
)}


        </div>

        {/* ── Session Notes ── */}
        <p className="cd-section-label">Session notes</p>
        <input
          type="text"
          className="cd-search-input"
          placeholder="🔍 Search transcripts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ marginBottom: '1rem', padding: '0.5rem 1rem', width: '100%', maxWidth: '300px', borderRadius: '8px', border: '1px solid #ddd' }}
        />
        <div className="cd-notes-layout">

          {/* Left: notes list — newest first */}
          <div className="cd-notes-list">
            {filteredSessionNotes?.length > 0 ? (
              [...filteredSessionNotes]
                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                .map((note, index) => (
                  <div
                    key={note._id}
                    className={`cd-note-item ${selectedNote?._id === note._id ? 'active' : ''}`}
                    onClick={() => { handleSelectNote(note); setActiveNoteTab('view'); }}
                  >
                    <div className="cd-note-title">
                      {note.title || `Session note ${clientData.sessionNotes.length - index}`}
                      <button
                        className="cd-rename-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setRenameModal({ show: true, noteId: note._id, currentTitle: note.title || `Session note ${clientData.sessionNotes.length - index}` });
                        }}
                        title="Rename"
                      >✏️</button>
                    </div>
                    <div className="cd-note-date">
                      {new Date(note.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      {' · '}
                      {new Date(note.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    {/* Show indicator dots if note has analysis */}
                    <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                      {(note.summary || noteSummaries[note._id]) && (
                        <span style={{ fontSize: '10px', color: '#E8654A' }}>✦ Summary</span>
                      )}
                      {(note.customAnalysis || customAnalyses[note._id]) && (
                        <span style={{ fontSize: '10px', color: '#8898AA' }}>⚙ Custom</span>
                      )}
                    </div>
                  </div>
                ))
            ) : (
              <div className="cd-notes-empty">
                <p>No session notes yet</p>
                <button className="cd-pill-btn cd-pill-accent" onClick={() => setActiveNoteTab('add')}>
                  + Add first note
                </button>
              </div>
            )}
          </div>

          {/* Right: tabbed panel */}
          <div className="cd-note-panel">
            <div className="cd-note-tabs">
              <button
                className={`cd-note-tab ${activeNoteTab === 'view' ? 'active' : ''}`}
                onClick={() => setActiveNoteTab('view')}
              >
                View note
              </button>
              <button
                className={`cd-note-tab ${activeNoteTab === 'add' ? 'active' : ''}`}
                onClick={() => setActiveNoteTab('add')}
              >
                + Add new
              </button>
            </div>

            {/* View tab */}
            {activeNoteTab === 'view' && (
              <div className="cd-note-view">
                {selectedNote ? (
                  isEditingNote ? (
                    <>
                      <p className="cd-card-label">Editing note</p>
                      <textarea
                        className="cd-note-textarea"
                        value={editNoteText}
                        onChange={(e) => setEditNoteText(e.target.value)}
                        rows="12"
                      />
                      <div className="cd-note-view-actions">
                        <button className="cd-save-btn" onClick={handleSaveEditedNote}>Save</button>
                        <button className="cd-cancel-btn" onClick={() => setIsEditingNote(false)}>Cancel</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="cd-note-view-header">
                        <h3>{selectedNote.title || 'Session note'}</h3>
                        <span className="cd-note-view-date">
                          {new Date(selectedNote.timestamp).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>

                      {/* Note text */}
                      <p className="cd-note-body">{selectedNote.text}</p>

                      {/* Summary result — shown inline when available */}
                      {(noteSummaries[selectedNote._id] || selectedNote.summary) && (
                        <div className="cd-analysis-block">
                          <p className="cd-analysis-label">✦ Summary</p>
                          <p className="cd-analysis-text">
                            {noteSummaries[selectedNote._id] || selectedNote.summary}
                          </p>
                          <div style={{ display: 'flex', gap: '6px', marginTop: '0.5rem' }}>
                              {(() => {
                                const currentSummary = noteSummaries[selectedNote._id] || selectedNote.summary;
                                const isAlreadySaved = !!selectedNote.summary && currentSummary === selectedNote.summary;
                                return (
                                  <button
                                    className="cd-small-btn"
                                    onClick={() => handleSaveSummary(selectedNote._id)}
                                    disabled={savingSummaryId === selectedNote._id || isAlreadySaved}
                                  >
                                    {savingSummaryId === selectedNote._id
                                      ? '⏳ Saving...'
                                      : isAlreadySaved
                                        ? '✓ Saved'
                                        : '💾 Save'}
                                  </button>
                                );
                              })()}
                            <button className="cd-small-btn" onClick={() => copyToClipboard(noteSummaries[selectedNote._id] || selectedNote.summary)}>📋 Copy</button>
                            <button className="cd-small-btn danger" onClick={() => handleDeleteSummary(selectedNote._id)}>🗑 Delete</button>
                          </div>
                        </div>
                      )}

                      {/* Action items — shown inline when available */}
                      {actionItems[selectedNote._id]?.length > 0 && (
                        <div className="cd-analysis-block">
                          <p className="cd-analysis-label">✓ Action items</p>
                          <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                            {actionItems[selectedNote._id].map((item, i) => (
                              <li key={i} style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                                <strong>{item.owner === 'coach' ? '🧑‍💼 Coach:' : '👤 Client:'}</strong> {item.task}
                                {item.due && <span style={{ color: '#8898AA', marginLeft: '0.5rem' }}>({item.due})</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Custom analysis result — shown inline when available */}
                      {(customAnalyses[selectedNote._id] || selectedNote.customAnalysis) && (
                        <div className="cd-analysis-block">
                          <p className="cd-analysis-label">⚙ Custom analysis</p>
                          <p className="cd-analysis-text">
                            {customAnalyses[selectedNote._id] || selectedNote.customAnalysis}
                          </p>
                          <div style={{ display: 'flex', gap: '6px', marginTop: '0.5rem' }}>
                            <button className="cd-small-btn" onClick={() => handleSaveCustomAnalysis(selectedNote._id)}>💾 Save</button>
                            <button className="cd-small-btn" onClick={() => copyToClipboard(customAnalyses[selectedNote._id] || selectedNote.customAnalysis)}>📋 Copy</button>
                            <button className="cd-small-btn danger" onClick={() => handleDeleteCustomAnalysis(selectedNote._id)}>🗑 Delete</button>
                          </div>
                        </div>
                      )}

                      {/* Action row — run analysis + edit/delete only */}
                      <div className="cd-note-view-actions">
                        {aiTemplates.length > 0 && (
                          <select
                            value={selectedTemplate}
                            onChange={(e) => setSelectedTemplate(e.target.value)}
                            style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.85rem', marginRight: '0.5rem' }}
                          >
                            <option value="">Default summary</option>
                            {aiTemplates.map((t) => (
                              <option key={t._id} value={t._id}>{t.name}</option>
                            ))}
                          </select>
                        )}
                        <button
                          className="cd-action-btn primary"
                          onClick={() => summarizeSingleNote(selectedNote.text, selectedNote._id)}
                          disabled={loadingSummaries[selectedNote._id]}
                        >
                          ✦ {loadingSummaries[selectedNote._id] ? 'Summarizing...' : 'Summary'}
                        </button>
                        {isPro ? (
                          <button
                            className="cd-action-btn"
                            onClick={() => handleCustomAnalysis(selectedNote.text, selectedNote._id)}
                            disabled={loadingCustomAnalyses[selectedNote._id]}
                          >
                            ⚙ {loadingCustomAnalyses[selectedNote._id] ? 'Analyzing...' : 'Custom analysis'}
                          </button>
                        ) : (
                          <Link to="/pricing" className="cd-action-btn locked">🔒 Custom analysis</Link>
                        )}
                        <button
                          className="cd-action-btn"
                          onClick={() => extractActionItems(selectedNote.text, selectedNote._id)}
                          disabled={loadingActions[selectedNote._id]}
                        >
                          ✓ {loadingActions[selectedNote._id] ? 'Extracting...' : 'Action items'}
                        </button>
                        <button className="cd-action-btn" style={{ marginLeft: 'auto' }} onClick={() => handleEditNote(selectedNote)}>✎ Edit</button>
                        <button
                          className="cd-action-btn"
                          onClick={() => {
                            navigator.clipboard.writeText(selectedNote.text);
                            setNotification('📋 Note copied to clipboard');
                            setTimeout(() => setNotification(''), 2500);
                          }}
                          title="Copy note text"
                        >📋 Copy</button>
                          <button
                          className="cd-action-btn"
                          onClick={() => setSendTranscriptModal({ show: true, noteId: selectedNote._id })}
                          title="Email transcript to client"
                        >
                          📧 Send to client
                        </button>
                        <button className="cd-action-btn danger" onClick={() => handleDeleteNote(selectedNote._id)}>🗑</button>
                      </div>
                    </>
                  )
                ) : (
                  <div className="cd-note-view-empty">
                    <p>Select a note on the left to view it</p>
                  </div>
                )}
              </div>
            )}

            {/* Add tab */}
            {activeNoteTab === 'add' && (
              <div className="cd-note-view">
                <p className="cd-card-label">New session note</p>
                <textarea
                  ref={noteTextareaRef}
                  className="cd-note-textarea"
                  placeholder="Add session notes here..."
                  value={newSessionNote}
                  onChange={(e) => setNewSessionNote(e.target.value)}
                  rows="10"
                />
                {renderRecordingControls()}
                <div className="cd-note-view-actions" style={{ marginTop: '0.75rem' }}>
                  <button className="cd-save-btn" onClick={() => { handleAddSessionNote(); setActiveNoteTab('view'); }}>
                    Save note
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── AI Tools ── */}
        <p className="cd-section-label" style={{ marginTop: '1.5rem' }}>AI tools</p>

        {/* Built-in AI Assistant */}
        <div className="cd-collapsible">
          <div className="cd-collapsible-header" onClick={() => requireConsent(() => setShowAIChat(!showAIChat))}>
            <span className="cd-collapsible-title">
              <span className="cd-tool-icon">✦</span>
              AI assistant
              <span className="cd-badge navy">Built-in</span>
            </span>
            <span className="cd-chevron">{showAIChat ? '▴' : '▾'}</span>
          </div>
          {showAIChat && (
            <div className="cd-collapsible-body">
              <div className="cd-ai-note-select">
                <div className="cd-ai-note-header">
                  <p className="cd-card-label">Select notes for context</p>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="cd-small-btn" onClick={handleSelectAllNotesForAI}>All</button>
                    <button className="cd-small-btn" onClick={handleClearSelectedNotes}>Clear</button>
                    <span className="cd-selected-count">{selectedNotesForAI.length} selected</span>
                  </div>
                </div>
                <div className="cd-ai-notes-list">
                  {clientData?.sessionNotes?.map((note) => (
                    <label key={note._id} className="cd-ai-note-row">
                      <input
                        type="checkbox"
                        checked={selectedNotesForAI.includes(note._id)}
                        onChange={() => handleToggleNoteForAI(note._id)}
                      />
                      <span className="cd-ai-note-date">
                        {new Date(note.timestamp).toLocaleDateString()}
                      </span>
                      <span className="cd-ai-note-preview">{note.text.substring(0, 60)}...</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="cd-chat-messages">
                {aiChatMessages.length === 0 ? (
                  <div className="cd-chat-welcome">
                    <p>Ask me anything about the selected session notes.</p>
                  </div>
                ) : (
                  aiChatMessages.map((msg, i) => (
                    <div key={i} className={`cd-chat-msg ${msg.role}`}>
                      <span className="cd-chat-role">{msg.role === 'user' ? '👤 You' : '✦ AI'}</span>
                      <p>{msg.content}</p>
                    </div>
                  ))
                )}
                {aiChatLoading && (
                  <div className="cd-chat-msg assistant">
                    <span className="cd-chat-role">✦ AI</span>
                    <p className="cd-typing">Thinking...</p>
                  </div>
                )}
              </div>

              <form onSubmit={handleAIChatSubmit} className="cd-chat-form">
                <textarea
                  ref={aiChatInputRef}
                  value={aiChatInput}
                  onChange={(e) => setAiChatInput(e.target.value)}
                  placeholder="Ask anything about the session notes..."
                  rows="3"
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAIChatSubmit(e); } }}
                />
                <button type="submit" className="cd-save-btn" disabled={aiChatLoading || !aiChatInput.trim()}>
                  {aiChatLoading ? 'Sending...' : 'Send'}
                </button>
              </form>

              {aiChatMessages.length > 0 && (
                <div className="cd-chat-actions">
                  <button className="cd-small-btn" onClick={handleSaveAIResponse} disabled={!aiChatResponse}>💾 Save as note</button>
                  <button className="cd-small-btn" onClick={handleCopyAIResponse} disabled={!aiChatResponse}>📋 Copy</button>
                  <button className="cd-small-btn" onClick={handleExportAIChat}>📤 Export</button>
                  <button className="cd-small-btn danger" onClick={handleClearAIChat}>🗑 Clear</button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* External AI Tool */}
        <div className="cd-collapsible">
          <div className="cd-collapsible-header" onClick={() => setShowChatGPTLauncher(!showChatGPTLauncher)}>
            <span className="cd-collapsible-title">
              <span className="cd-tool-icon">↗</span>
              External AI tool
            </span>
            <span className="cd-chevron">{showChatGPTLauncher ? '▴' : '▾'}</span>
          </div>
          {showChatGPTLauncher && (
            <div className="cd-collapsible-body">
              <p className="cd-ext-ai-hint">Paste your preferred AI tool URL — ChatGPT, Claude, Gemini, Perplexity, or any other. Copy session notes above and paste them in.</p>
              <div className="cd-ext-ai-row">
                <input
                  className="cd-ext-ai-input"
                  value={externalAIUrl}
                  onChange={(e) => setExternalAIUrl(e.target.value)}
                  placeholder="https://chat.openai.com"
                />
                <button
                  className="cd-small-btn"
                  onClick={() => externalAIUrl && window.open(externalAIUrl, '_blank')}
                  disabled={!externalAIUrl}
                >
                  Open
                </button>
                <button
                  className="cd-small-btn"
                  onClick={handleSaveExternalAI}
                  disabled={savingExternalAI}
                >
                  {savingExternalAI ? 'Saving...' : 'Save default'}
                </button>
              </div>

              {/* Quick copy notes */}
              <div className="cd-ext-notes-list">
                <div className="cd-ai-note-header" style={{ marginBottom: '0.5rem' }}>
                  <p className="cd-card-label">Quick copy notes</p>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="cd-small-btn" onClick={handleSelectAllNotesForAI}>All</button>
                    <button className="cd-small-btn" onClick={handleClearSelectedNotes}>Clear</button>
                  </div>
                </div>
                {clientData?.sessionNotes?.map((note, idx) => (
                  <div key={note._id} className="cd-ext-note-row">
                    <input
                      type="checkbox"
                      checked={selectedNotesForAI.includes(note._id)}
                      onChange={() => handleToggleNoteForAI(note._id)}
                    />
                    <span className="cd-ext-note-title">{note.title || `Session ${idx + 1}`}</span>
                    <button
                      className={`cd-small-btn ${copiedNoteId === note._id ? 'copied' : ''}`}
                      onClick={() => copyNoteForChatGPT(note.text, note._id)}
                    >
                      {copiedNoteId === note._id ? '✓ Copied' : '📋 Copy'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="cd-nav-footer">
          <button className="cd-pill-btn" onClick={() => navigate('/clients')}>Done</button>
          <button className="cd-pill-btn" onClick={() => navigate('/dashboard')}>Back to dashboard</button>
        </div>
      </>
    ) : (
      <div className="cd-loading">Loading client data...</div>
    )}

    {/* ── Transcription/Note Saved Modal ── */}
    {showSavedModal && (
      <div style={{
        position: 'fixed',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 9999,
        background: '#fff',
        borderRadius: '20px',
        padding: '2rem 2.5rem',
        textAlign: 'center',
        boxShadow: '0 20px 60px rgba(15,28,48,0.2)',
        border: '0.5px solid rgba(27,43,75,0.1)',
        minWidth: '280px',
        animation: 'cd-fade-in 0.2s ease',
      }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>✅</div>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1B2B4B', margin: '0 0 0.4rem' }}>
          Saved!
        </h3>
        <p style={{ fontSize: '0.9rem', color: '#8898AA', margin: 0 }}>
          {savedModalMessage}
        </p>
      </div>
    )}

    {showSavedModal && (
      <div style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15,28,48,0.3)',
        backdropFilter: 'blur(3px)',
        zIndex: 9998,
      }} />
    )}

    {/* ── Billing Prompt Modal ── */}
    {showBillingPrompt && billingPromptData && (
      <div style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15,28,48,0.5)',
        backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9998, padding: '1rem',
      }} onClick={() => { setShowBillingPrompt(false); setBillingPromptData(null); }}>
        <div style={{
          background: '#FAFBFD',
          borderRadius: '20px',
          padding: '2rem',
          maxWidth: '380px', width: '100%',
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(15,28,48,0.2)',
          position: 'relative',
          animation: 'cd-fade-in 0.2s ease',
        }} onClick={e => e.stopPropagation()}>
          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '4px', background: 'linear-gradient(90deg, #1B2B4B, #E8654A)', borderRadius: '20px 20px 0 0' }} />
          <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>💳</div>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1B2B4B', margin: '0 0 0.4rem' }}>
            Create billing record?
          </h3>
          <p style={{ fontSize: '0.9rem', color: '#8898AA', margin: '0 0 0.25rem' }}>
            {billingPromptData.sessionTitle}
          </p>
          <p style={{ fontSize: '1.4rem', fontWeight: 700, color: '#1B2B4B', margin: '0.5rem 0 1.5rem' }}>
            ${billingPromptData.rate?.toFixed(2)}
            {billingPromptData.durationMinutes > 0 && (
              <span style={{ fontSize: '0.85rem', fontWeight: 400, color: '#8898AA', marginLeft: '6px' }}>
                · {billingPromptData.durationMinutes} min
              </span>
            )}
          </p>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={handleCreateBillingRecord}
              style={{ flex: 1, padding: '10px', background: '#1B2B4B', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Yes, add it
            </button>
            <button
              onClick={() => { setShowBillingPrompt(false); setBillingPromptData(null); }}
              style={{ flex: 1, padding: '10px', background: 'rgba(27,43,75,0.06)', color: '#8898AA', border: '0.5px solid rgba(27,43,75,0.15)', borderRadius: '10px', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Skip
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Rename modal */}
    {renameModal.show && ReactDOM.createPortal(
      <div style={{ position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'rgba(15,28,48,0.5)',backdropFilter:'blur(6px)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999 }}
        onClick={() => setRenameModal({ show: false, noteId: null, currentTitle: '' })}>
        <div className="cd-modal" onClick={e => e.stopPropagation()}>
          <h2>Rename session note</h2>
          <p>Give this session a meaningful title for easy reference.</p>
          <input
            type="text"
            className="cd-modal-input"
            value={renameModal.currentTitle}
            onChange={(e) => setRenameModal(prev => ({ ...prev, currentTitle: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { handleRenameNote(renameModal.noteId, renameModal.currentTitle); setRenameModal({ show: false, noteId: null, currentTitle: '' }); }
              if (e.key === 'Escape') setRenameModal({ show: false, noteId: null, currentTitle: '' });
            }}
            autoFocus maxLength={100} placeholder="e.g. Goal setting — Q1 review"
          />
          <div className="cd-modal-actions">
            <button className="cd-cancel-btn" onClick={() => setRenameModal({ show: false, noteId: null, currentTitle: '' })}>Cancel</button>
            <button className="cd-save-btn" onClick={() => { handleRenameNote(renameModal.noteId, renameModal.currentTitle); setRenameModal({ show: false, noteId: null, currentTitle: '' }); }}>Save</button>
          </div>
        </div>
      </div>,
      document.body
    )}

    {/* Confirm modal */}
    {confirmModal.show && ReactDOM.createPortal(
      <div style={{ position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'rgba(15,28,48,0.5)',backdropFilter:'blur(6px)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999 }}
        onClick={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: null })}>
        <div className="cd-modal" onClick={e => e.stopPropagation()}>
          <h2>{confirmModal.title}</h2>
          <p>{confirmModal.message}</p>
          <div className="cd-modal-actions">
            <button className="cd-cancel-btn" onClick={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: null })}>Cancel</button>
            <button className="cd-save-btn danger" onClick={() => confirmModal.onConfirm?.()}>
              {confirmModal.title === 'Log Out' ? 'Log Out' : 'Delete'}
            </button>
          </div>
        </div>
      </div>,
      document.body
    )}

    {/* Session expiration modal */}
    {showSessionModal && (
      <div className="session-modal" style={{ display: 'block' }}>
        <div className="session-modal-content">
          <h2>Your session is about to expire!</h2>
          <p>You have less than 2 minutes left before being logged out. Would you like to stay logged in?</p>
          <button onClick={handleStayLoggedIn} className="cd-save-btn">Stay logged in</button>
          <button onClick={handleLogout} className="cd-cancel-btn" style={{ marginLeft: '0.5rem' }}>Log out</button>
        </div>
      </div>
    )}
{/* Transcription Consent Reminder */}
{showTranscriptionConsentReminder && (
  <div style={{
    position: 'fixed', inset: 0,
    background: 'rgba(27,43,75,0.5)',
    backdropFilter: 'blur(4px)',
display: 'flex', alignItems: 'center', justifyContent: 'center',
zIndex: 9999, padding: '0 20px',
  }}>
    <div style={{
      background: '#fff',
      borderRadius: '24px',
      padding: '24px 24px 36px',
      width: '100%', maxWidth: '480px',
      animation: 'cd-fade-in 0.2s ease',
    }}>
      <div style={{ width: 40, height: 4, background: '#E9ECEF', borderRadius: 2, margin: '0 auto 20px' }} />
      <div style={{ fontSize: '2rem', textAlign: 'center', marginBottom: '12px' }}>🎙️</div>
      <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1B2B4B', textAlign: 'center', margin: '0 0 8px' }}>
        Quick check-in
      </h3>
      <p style={{ fontSize: '0.9rem', color: '#6C757D', textAlign: 'center', lineHeight: 1.6, margin: '0 0 20px' }}>
        Has <strong style={{ color: '#1B2B4B' }}>
          {clientData?.firstName || 'your client'}
        </strong> been informed that this session will be transcribed by AI?
      </p>
      <button
        onClick={() => {
          setShowTranscriptionConsentReminder(false);
          if (pendingRecordingFn) { pendingRecordingFn(); setPendingRecordingFn(null); }
        }}
        style={{
          width: '100%', padding: '14px', marginBottom: '8px',
          background: '#E8654A', color: '#fff', border: 'none',
          borderRadius: '12px', fontSize: '15px', fontWeight: 600,
          cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        ✓ Yes — start transcribing
      </button>
      <button
        onClick={() => { setShowTranscriptionConsentReminder(false); setPendingRecordingFn(null); }}
        style={{
          width: '100%', padding: '12px',
          background: 'transparent', color: '#6C757D', border: 'none',
          fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        Not yet — I'll tell them first
      </button>
    </div>
  </div>
)}

{sendTranscriptModal.show && ReactDOM.createPortal(
  <div style={{ position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'rgba(15,28,48,0.5)',backdropFilter:'blur(6px)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999 }}
    onClick={() => setSendTranscriptModal({ show: false, noteId: null })}>
    <div className="cd-modal" onClick={e => e.stopPropagation()}>
      <h2>Send transcript to client</h2>
      <p>
        This will email the full session transcript to{' '}
        <strong>{clientData?.firstName} {clientData?.lastName}</strong> at{' '}
        <strong>{clientData?.email}</strong>.
      </p>
      <div className="cd-modal-actions">
        <button className="cd-cancel-btn" onClick={() => setSendTranscriptModal({ show: false, noteId: null })}>
          Cancel
        </button>
        <button
          className="cd-save-btn"
          disabled={sendingTranscript}
          onClick={() => handleSendTranscript(sendTranscriptModal.noteId)}
        >
          {sendingTranscript ? 'Sending...' : 'Send transcript'}
        </button>
      </div>
    </div>
  </div>,
  document.body
)}
    {/* AI Consent Modal */}
    {showConsentModal && <AIConsentModal onAccept={handleAccept} onDecline={handleDecline} />}
  </div>
);
};

export default ClientDashboard;